/**
 * Hero-power adapter — thin v2 bridge over the v1 `heroPowers.ts`
 * registry. The match store calls `classifyPower` to decide what UX
 * to drive (no-target / targeted / discover) and then calls the
 * matching `apply*` function to mutate state.
 *
 * Design choice: rather than duplicate v1's per-hero implementations,
 * we sync the human's v2 snapshot into the v1 `gameState.players[0]`
 * singleton, run a minimal v1-flavoured mutation locally (the active
 * powers are tiny — +1/+1, double-hp, swap stats, freeze shop), then
 * read back. v1's `heroPowers.ts` itself only exposes passive entry
 * points — actives in v1 are wired straight into TavernScene. We
 * keep that boundary by inlining the small active-power effects here
 * rather than reaching into v1 UI code.
 *
 * Powers handled in v2:
 *   - piercing_artist     active_blood_price   target=unit  cost=0 (+2 HP)
 *   - tattoo_master       active_health_double target=unit  cost=2
 *   - don_hidraulico      active_stat_swap     target=unit  cost=3
 *   - detail_specialist   active_freeze_shop_unit target=shop cost=1
 *   - guitar_teacher      active_reckless_buff target=unit  cost=1
 *   - la_aventada         active_move_and_buff target=unit  cost=1
 *   - el_generador        active_crew_surge    no target    cost=2
 *
 * The passive powers all classify as `'passive'` here and the store
 * treats `activateHeroPower` as a no-op — they already fire through
 * the v1 engine via `syncHumanToV1` pre-combat.
 *
 * BANK vs BURN in this file: a bank power writes straight to
 * `attack`/`health`, a burn power writes to `combatBuff`, which
 * `simulateCombat` folds into its sim copies and the round commit
 * throws away (`clearCombatBuffs`). That field is the whole reason an
 * active can be temporary without any new machinery.
 */

import type { Card } from './data/cards/types';
import type { Hero, HeroPowerType } from './data/heroes';
import { allHeroes, type HeroId } from './data/heroes';

export type PowerVariant =
  | 'passive'
  | 'active'
  | 'targeted_unit'
  | 'targeted_shop'
  | 'discover';

export interface PowerClassification {
  variant: PowerVariant;
  cost: number;
  targetType?: 'unit' | 'shop';
}

/** Lookup a hero by id from the master registry. */
export function getHeroById(id: HeroId | null | undefined): Hero | null {
  if (!id) return null;
  return allHeroes.find((h) => h.id === id) ?? null;
}

/**
 * Categorize a hero's power so the store can route the UI:
 *  - passive        -> button stays disabled; existing engine handles it.
 *  - active         -> apply immediately, no target needed.
 *  - targeted_unit  -> wait for the player to tap a board slot.
 *  - targeted_shop  -> wait for the player to tap a shop card.
 *  - discover       -> show 3-card picker (none today, reserved).
 */
export function classifyPower(
  type: HeroPowerType,
  /**
   * The carrying hero's own `cost`, when the caller has it. The table below
   * is a default, not the truth: the player reads the price off the hero
   * card, so the hero card has to be what the store charges. Don Hidráulico
   * would otherwise have been billed the 0g that the bench's free stat-swap
   * hero left behind in this table.
   */
  cost?: number,
): PowerClassification {
  const priced = (c: PowerClassification): PowerClassification =>
    cost === undefined ? c : { ...c, cost };
  return priced(classifyPowerDefaults(type));
}

function classifyPowerDefaults(type: HeroPowerType): PowerClassification {
  switch (type) {
    case 'active_target_buff':       // unclaimed since La Aguja's re-roll
      return { variant: 'targeted_unit', cost: 1, targetType: 'unit' };
    case 'active_health_double':     // tattoo_master (2g)
      return { variant: 'targeted_unit', cost: 2, targetType: 'unit' };
    case 'active_stat_swap':         // don_hidraulico (3g)
      return { variant: 'targeted_unit', cost: 3, targetType: 'unit' };
    case 'active_freeze_shop_unit':  // detail_specialist (1g)
      return { variant: 'targeted_shop', cost: 1, targetType: 'shop' };
    case 'active_reckless_buff':     // guitar_teacher / El Telonero (1g)
      return { variant: 'targeted_unit', cost: 1, targetType: 'unit' };
    case 'active_blood_price':       // piercing_artist / La Aguja (0g + Health)
      // Free in gold on purpose — `powerConfig.healthCost` is the real
      // price, and the store checks it before arming the power.
      return { variant: 'targeted_unit', cost: 0, targetType: 'unit' };
    case 'active_move_and_buff':     // la_aventada / La Aventada (1g)
      return { variant: 'targeted_unit', cost: 1, targetType: 'unit' };
    case 'active_crew_surge':        // el_generador / El del Generador (2g)
      return { variant: 'active', cost: 2 };
    default:
      return { variant: 'passive', cost: 0 };
  }
}

/**
 * Stack a "this combat only" buff onto a card without touching its real
 * stats. Additive, so two sources in one turn both land.
 */
function withCombatBuff(unit: Card, attack: number, health: number): Card {
  const prev = unit.combatBuff ?? { attack: 0, health: 0 };
  return {
    ...unit,
    combatBuff: { attack: prev.attack + attack, health: prev.health + health },
  };
}

/**
 * A minimal snapshot of the v2 store that the adapter reads. Keeps
 * this module decoupled from `match.ts` so it stays unit-testable.
 */
export interface MatchSnapshot {
  heroId: HeroId | null;
  gold: number;
  /** Read by the blood-price powers, which charge Health instead of gold.
      Optional so callers written before those powers still compile; they
      simply never pass the affordability check. */
  health?: number;
  board: (Card | null)[];
  shop: Card[];
  shopFrozen: boolean;
}

/**
 * A patch the adapter returns when a power resolves. The store
 * `set()`s this onto its own state. Only includes fields that
 * changed.
 */
export interface MatchPatch {
  gold?: number;
  /** Only set by powers that charge Health. The store applies it verbatim. */
  health?: number;
  board?: (Card | null)[];
  shop?: Card[];
  shopFrozen?: boolean;
}

/**
 * Apply a no-target active power.
 *
 * Returns null if the hero or power is unhandled; caller treats null
 * as "do nothing".
 */
export function applyActivePower(state: MatchSnapshot): MatchPatch | null {
  const hero = getHeroById(state.heroId);
  if (!hero) return null;
  const cfg = hero.powerConfig || {};

  switch (hero.powerType) {
    case 'active_crew_surge': {
      // El del Generador: everyone on the board goes loud for one combat.
      // Board-wide and temporary, so it wants an empty-board guard —
      // otherwise the player can burn 2g on nothing.
      const atk = cfg.attack ?? 0;
      const hp = cfg.health ?? 0;
      if (atk === 0 && hp === 0) return null;
      if (!state.board.some((c) => c !== null)) return null;
      return {
        board: state.board.map((c) => (c ? withCombatBuff(c, atk, hp) : c)),
      };
    }
    default:
      return null;
  }
}

/**
 * Apply a targeted power against a board slot or a shop card index.
 * Returns a patch describing the mutated state, or null if the
 * target was invalid (caller will then keep the targeting UI live).
 *
 * Cost is NOT deducted here — the store handles gold so it can
 * cancel-refund cleanly. We just mutate stats / freeze flag.
 */
export function applyTargetedPower(
  state: MatchSnapshot,
  targetIdx: number,
): MatchPatch | null {
  const hero = getHeroById(state.heroId);
  if (!hero) return null;
  const { variant, targetType } = classifyPower(hero.powerType);

  if (variant === 'targeted_unit' && targetType === 'unit') {
    if (targetIdx < 0 || targetIdx >= state.board.length) return null;
    const unit = state.board[targetIdx];
    if (!unit) return null;
    let nextUnit: Card = { ...unit };
    const cfg = hero.powerConfig || {};

    // Board-shape powers rewrite slot order, so they can't go through the
    // "replace one slot" path the stat powers share below.
    if (hero.powerType === 'active_move_and_buff') {
      // La Aventada: she goes over the barrier to the front. Splice-then-
      // unshift keeps the array length and every other unit's relative
      // order intact — only the units she jumped move, and they move back
      // one slot each. Re-aiming her at the unit already in front is a
      // no-op move but still a legal buy for the buff.
      const nextBoard = [...state.board];
      nextBoard.splice(targetIdx, 1);
      nextBoard.unshift(withCombatBuff(nextUnit, cfg.attack ?? 0, cfg.health ?? 0));
      return { board: nextBoard };
    }

    switch (hero.powerType) {
      case 'active_target_buff': {
        // Piercing Artist: +1/+1 permanently.
        nextUnit.attack += cfg.attack ?? 0;
        nextUnit.health += cfg.health ?? 0;
        break;
      }
      case 'active_health_double': {
        // Tattoo Master: double health if attack <= maxAttack.
        const maxAttack = cfg.maxAttack ?? 5;
        if (nextUnit.attack > maxAttack) return null; // invalid target
        nextUnit.health = nextUnit.health * 2;
        break;
      }
      case 'active_reckless_buff': {
        // El Telonero: buy Attack with Health. Refuse targets the trade
        // would kill outright — a power that can delete your own unit on a
        // mistap is a trap, not a decision. The guard mirrors
        // active_health_double's, so the targeting UI already handles the
        // "invalid target, stay live" path.
        const dAtk = cfg.attack ?? 0;
        const dHp = cfg.health ?? 0;
        if (nextUnit.health + dHp < 1) return null;
        nextUnit.attack = Math.max(0, nextUnit.attack + dAtk);
        nextUnit.health += dHp;
        break;
      }
      case 'active_blood_price': {
        // La Aguja: Health is the price. Charged here rather than by the
        // store's gold path, and refused outright when it would kill the
        // player — a hero power must never be the thing that ends the run.
        const cost = cfg.healthCost ?? 0;
        const current = state.health ?? 0;
        if (cost > 0 && current - cost < 1) return null;
        nextUnit = withCombatBuff(nextUnit, cfg.attack ?? 0, cfg.health ?? 0);
        const bloodBoard = [...state.board];
        bloodBoard[targetIdx] = nextUnit;
        return { board: bloodBoard, health: current - cost };
      }
      case 'active_stat_swap': {
        // Don Hidráulico: swap attack and health, then bolt on `buffAfter`
        // if the hero's config asks for it. Permanent — this is the cholo
        // BANK counterpart to the emo actives, so it writes real stats and
        // never touches combatBuff.
        const a = nextUnit.attack;
        nextUnit.attack = nextUnit.health;
        nextUnit.health = a;
        const after = cfg.buffAfter;
        if (after) {
          nextUnit.attack += after.attack ?? 0;
          nextUnit.health += after.health ?? 0;
        }
        break;
      }
      default:
        return null;
    }

    const nextBoard = [...state.board];
    nextBoard[targetIdx] = nextUnit;
    return { board: nextBoard };
  }

  if (variant === 'targeted_shop' && targetType === 'shop') {
    if (targetIdx < 0 || targetIdx >= state.shop.length) return null;
    const shopCard = state.shop[targetIdx];
    if (!shopCard) return null;

    switch (hero.powerType) {
      case 'active_freeze_shop_unit': {
        // Detail Specialist: freeze a single shop unit. v2's shop
        // freeze is whole-shop, not per-card. Closest semantic: flip
        // shopFrozen so the next refresh keeps this slot too. The
        // store's existing `toggleFreeze` already supports this.
        return { shopFrozen: true };
      }
      default:
        return null;
    }
  }

  return null;
}

/**
 * Build the 3-card pool a discover-variant power offers. No discover
 * powers in v2 today — keeps the contract complete so Agent H's UI
 * doesn't need a separate codepath for "future powers".
 */
export function buildDiscoverOptions(
  _state: MatchSnapshot,
  _rng: { next(): number; int(n: number): number },
): Card[] {
  return [];
}
